/**
 * useAttentionChannel (KAN-706)
 *
 * Selected tab of the "Требуют внимания" card. The choice is kept in
 * localStorage (best effort: storage may be blocked). Without a stored choice
 * the default is Telegram; the first totals with zero Telegram rows and some
 * call rows switch the tab to calls once (see `autoPick`).
 */
import { useCallback, useRef, useState } from 'react'
import type { AttentionChannel, AttentionTotals } from '@/services/api/dashboard'

export const ATTENTION_CHANNEL_STORAGE_KEY = 'dashboard-attention-channel'

function readStored(): AttentionChannel | null {
  try {
    const value = window.localStorage.getItem(ATTENTION_CHANNEL_STORAGE_KEY)
    return value === 'telegram' || value === 'call' ? value : null
  } catch {
    return null
  }
}

function writeStored(channel: AttentionChannel): void {
  try {
    window.localStorage.setItem(ATTENTION_CHANNEL_STORAGE_KEY, channel)
  } catch {
    // storage unavailable: the choice lives until reload
  }
}

export function useAttentionChannel() {
  const [initial] = useState(readStored)
  const [channel, setChannelState] = useState<AttentionChannel>(initial ?? 'telegram')
  // Auto-pick only while the user has no stored or explicit choice.
  const decided = useRef(initial !== null)

  const setChannel = useCallback((next: AttentionChannel) => {
    decided.current = true
    setChannelState(next)
    writeStored(next)
  }, [])

  /** Call with the first totals of the default tab; not persisted. */
  const autoPick = useCallback((totals: AttentionTotals | undefined) => {
    if (decided.current || !totals) return
    decided.current = true
    if (totals.telegram === 0 && totals.call > 0) setChannelState('call')
  }, [])

  return { channel, setChannel, autoPick }
}
